/**
 * Staff Guide: Requesting Time Off (PTO)
 * Simple one-page printable PDF guide
 */
import { Printer, Calendar, CheckCircle, AlertTriangle, Send } from 'lucide-react';

export default function StaffTimeOffGuide() {
  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
      `}</style>

      <div className="min-h-screen bg-white">
        {/* Header with Print Button */}
        <div className="no-print bg-orange-600 text-white py-4 px-6 flex justify-between items-center sticky top-0 z-50">
          <div className="flex items-center gap-3">
            <Calendar className="w-6 h-6" />
            <div>
              <h1 className="font-bold text-lg">Time Off Guide</h1>
              <p className="text-orange-100 text-sm">Request PTO / Days Off</p>
            </div>
          </div>
          <button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-white text-orange-600 px-4 py-2 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
          >
            <Printer className="w-4 h-4" />
            Print / Download PDF
          </button>
        </div>

        {/* Content */}
        <div className="max-w-2xl mx-auto p-8">
          {/* Title */}
          <div className="text-center mb-8 pb-6 border-b-2 border-orange-500">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Requesting Time Off</h1>
            <p className="text-xl text-orange-600">How to Request PTO / Days Off</p>
            <p className="text-gray-500 mt-2">Bulaccino Cafe - Nadi, Fiji</p>
          </div>

          {/* Important Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-8">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-bold text-amber-800 text-lg mb-2">Important!</h2>
                <ul className="text-amber-700 space-y-1">
                  <li>Request time off at least <strong>1 week in advance</strong></li>
                  <li>Requests are NOT automatically approved</li>
                  <li>Wait for manager approval before making plans</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-6 mb-8">
            {/* Step 1 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">1</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Go to the Staff Portal</h3>
                <p className="text-gray-600 mb-2">Open your phone browser and go to:</p>
                <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-3 text-center">
                  <p className="text-xl font-bold text-orange-600">bulaccinocafe.com/staff</p>
                </div>
              </div>
            </div>

            {/* Step 2 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">2</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Log In With Your PIN</h3>
                <p className="text-gray-600">Enter your 6-digit staff PIN to access your dashboard.</p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">3</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Tap "Request Time Off"</h3>
                <p className="text-gray-600 mb-3">Find the Time Off section on your dashboard.</p>
                <div className="bg-gray-100 rounded-lg p-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <Send className="w-5 h-5 text-orange-600" />
                  </div>
                  <span className="font-semibold text-gray-700">Request Time Off</span>
                </div>
              </div>
            </div>

            {/* Step 4 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">4</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Fill Out the Form</h3>
                <p className="text-gray-600 mb-3">Enter the required information:</p>
                
                <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-600 block mb-1">Start Date</label>
                    <div className="border border-gray-300 rounded px-3 py-2 text-gray-500">Select the first day off...</div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600 block mb-1">End Date</label>
                    <div className="border border-gray-300 rounded px-3 py-2 text-gray-500">Select the last day off...</div>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-gray-600 block mb-1">Reason</label>
                    <div className="border border-gray-300 rounded px-3 py-2 text-gray-500">e.g., Family event, vacation, medical...</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Step 5 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">5</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Submit Your Request</h3>
                <p className="text-gray-600">Tap the "Submit Request" button. Your manager will review it.</p>
              </div>
            </div>

            {/* Step 6 */}
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0">6</div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">Wait for Approval</h3>
                <p className="text-gray-600 mb-3">Check your dashboard to see if your request was:</p>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-green-700">Approved</span>
                    <span className="text-green-600 text-sm">- You're good to go!</span>
                  </div>
                  <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
                    <div className="w-5 h-5 rounded-full bg-amber-400"></div>
                    <span className="font-semibold text-amber-700">Pending</span>
                    <span className="text-amber-600 text-sm">- Still being reviewed</span>
                  </div>
                  <div className="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg px-4 py-2">
                    <div className="w-5 h-5 rounded-full bg-red-400"></div>
                    <span className="font-semibold text-red-700">Denied</span>
                    <span className="text-red-600 text-sm">- Talk to your manager</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
            <h2 className="font-bold text-orange-800 text-lg mb-4">Tips for Getting Approved</h2>
            <ul className="space-y-3">
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Request <strong>at least 1 week ahead</strong> for better chances</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Avoid requesting off on <strong>busy days</strong> (weekends, holidays)</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">Give a <strong>clear reason</strong> for your request</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                <span className="text-gray-700">If urgent, <strong>talk to your manager in person</strong> too</span>
              </li>
            </ul>
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t border-gray-200 text-center text-gray-500 text-sm">
            <p>Bulaccino Cafe - Nadi, Fiji</p>
            <p>Questions? Ask your manager or supervisor.</p>
          </div>
        </div>
      </div>
    </>
  );
}
